import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { createJSONEditor, createAjvValidator } from 'vanilla-jsoneditor'

/**
 * JSON editor component backed by vanilla-jsoneditor.
 * Supports optional JSON Schema validation and multiple editing modes.
 * @param {object} props
 * @param {*} props.value - JSON value to edit
 * @param {Function} props.onChange - Called with (parsedJson, contentErrors) on edits
 * @param {object} [props.schema] - JSON Schema for validation
 * @param {object} [props.schemaDefinitions] - Shared schema definitions ($defs)
 * @param {string} [props.mode='text'] - Editor mode ('text' or 'tree')
 * @param {boolean} [props.readOnly=false] - Whether the editor is read-only
 * @param {object} [props.style] - Additional styles for the container
 */
const JsonEditor = ({
  value,
  onChange,
  schema,
  schemaDefinitions,
  mode = 'text',
  mainMenuBar = false,
  navigationBar = false,
  statusBar = true,
  readOnly = false,
  style,
}) => {
  const containerRef = useRef(null)
  const editorRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    const props = {
      content: { json: value },
      mode,
      mainMenuBar,
      navigationBar,
      statusBar,
      readOnly,
      onChange: (updatedContent, previousContent, { contentErrors }) => {
        if (!onChangeRef.current) return
        if (updatedContent.json !== undefined) {
          onChangeRef.current(updatedContent.json, contentErrors)
        } else if (updatedContent.text !== undefined) {
          try {
            const parsed = JSON.parse(updatedContent.text)
            onChangeRef.current(parsed, contentErrors)
          } catch {
            onChangeRef.current(updatedContent.text, contentErrors)
          }
        }
      },
    }

    if (schema) {
      props.validator = createAjvValidator({
        schema,
        schemaDefinitions,
      })
    }

    editorRef.current = createJSONEditor({
      target: containerRef.current,
      props,
    })

    return () => {
      if (editorRef.current) {
        editorRef.current.destroy()
        editorRef.current = null
      }
    }
    // Initialization-only: creates the editor instance once on mount.
    // Prop changes are handled by the separate useEffects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (editorRef.current) {
      const props = { mode, mainMenuBar, navigationBar, statusBar, readOnly }
      if (schema) {
        props.validator = createAjvValidator({ schema, schemaDefinitions })
      }
      editorRef.current.updateProps(props)
    }
  }, [
    mode,
    mainMenuBar,
    navigationBar,
    statusBar,
    readOnly,
    schema,
    schemaDefinitions,
  ])

  useEffect(() => {
    if (!editorRef.current) return
    const current = editorRef.current.get()
    const currentJson =
      current.json !== undefined
        ? current.json
        : (() => {
            try {
              return JSON.parse(current.text)
            } catch {
              return undefined
            }
          })()
    if (JSON.stringify(currentJson) !== JSON.stringify(value)) {
      editorRef.current.set({ json: value })
    }
  }, [value])

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: 100,
        ...style,
      }}
    />
  )
}

JsonEditor.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  schema: PropTypes.object,
  schemaDefinitions: PropTypes.object,
  mode: PropTypes.oneOf(['text', 'tree']),
  mainMenuBar: PropTypes.bool,
  navigationBar: PropTypes.bool,
  statusBar: PropTypes.bool,
  readOnly: PropTypes.bool,
  style: PropTypes.object,
}

export default JsonEditor
